/**
 * Conversión entre instantes (UTC) y hora de pared en una zona IANA,
 * cálculo del día lógico (FR-TIME-01) y del instante de un recordatorio.
 * Usa solo Intl, disponible en navegadores, Node y JavaScriptCore.
 */
import { addDays, formatLocalDate, parseLocalDate, type LocalDate } from './dates.js';

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(tz, f);
  }
  return f;
}

export function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || tz.length === 0 || tz.length > 64) return false;
  try {
    formatterFor(tz);
    return true;
  } catch {
    return false;
  }
}

export interface WallClock {
  date: LocalDate;
  /** Minutos desde las 00:00 locales (0–1439). */
  minutes: number;
  seconds: number;
}

export function wallClock(instantMs: number, tz: string): WallClock {
  const parts = formatterFor(tz).formatToParts(new Date(instantMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const hour = get('hour') % 24;
  return {
    date: formatLocalDate(get('year'), get('month'), get('day')),
    minutes: hour * 60 + get('minute'),
    seconds: get('second'),
  };
}

/** Día lógico de un instante: fecha local de (hora de pared − inicio del día). */
export function logicalDateFor(instantMs: number, tz: string, dayStartMinutes: number): LocalDate {
  const wc = wallClock(instantMs, tz);
  return wc.minutes < dayStartMinutes ? addDays(wc.date, -1) : wc.date;
}

function offsetMs(instantMs: number, tz: string): number {
  const wc = wallClock(instantMs, tz);
  const { y, m, d } = parseLocalDate(wc.date);
  const asUtc = Date.UTC(y, m - 1, d, Math.floor(wc.minutes / 60), wc.minutes % 60, wc.seconds);
  return asUtc - Math.floor(instantMs / 1000) * 1000;
}

/**
 * Instante UTC correspondiente a la hora de pared `minutes` del día `date` en `tz`.
 * Si la hora no existe (salto de horario de verano), se desplaza hacia delante
 * la duración del salto; si es ambigua, se devuelve una de las dos ocurrencias.
 */
export function zonedTimeToInstant(date: LocalDate, minutes: number, tz: string): number {
  const { y, m, d } = parseLocalDate(date);
  const guess = Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60);
  const o1 = offsetMs(guess, tz);
  let t = guess - o1;
  const o2 = offsetMs(t, tz);
  if (o2 !== o1) {
    const t2 = guess - o2;
    t = Math.min(t, t2);
    const wc = wallClock(t, tz);
    if (wc.date !== date || wc.minutes !== minutes) t = Math.max(guess - o1, t2);
  }
  return t;
}

export function parseTimeOfDay(hhmm: string): number {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!match) throw new Error(`Hora no válida: ${hhmm}`);
  return Number(match[1]) * 60 + Number(match[2]);
}
